import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import moment from 'moment';
import { TMSService } from '../../core/service/tms/tms.service';
import { LocalStorageService } from '../../core/service/localStorage/local-storage.service';

// Lets callers check whether a day has any tasks/calendar events before
// opening the today-overview dialog or the sign-off catch-up flow, so
// neither shows up empty.
@Injectable({ providedIn: 'root' })
export class TodayOverviewService {
  private tmsService = inject(TMSService);
  private localStorageService = inject(LocalStorageService);

  // Used to gate the post-login/start-of-day "Today's Overview" dialog.
  hasTodayData(): Observable<boolean> {
    return from(this.localStorageService.getKeyIndexedDbKey('currentUser')).pipe(
      switchMap(currentUser => this._hasData(currentUser))
    );
  }

  // Used to gate the missed-sign-off catch-up flow — checks the same
  // lastActiveDate SignOffWrapupComponent would report on, so the wrap-up
  // page is only shown when there's actually something to catch up on.
  hasCatchUpSignOffData(): Observable<boolean> {
    return from(this.localStorageService.getKeyIndexedDbKey('currentUser')).pipe(
      switchMap(currentUser => {
        const lastActiveDate = currentUser?.lastActiveDate
          ? moment(currentUser.lastActiveDate).format('YYYY-MM-DD')
          : undefined;
        return this._hasData(currentUser, lastActiveDate);
      })
    );
  }

  private _hasData(currentUser: any, lastActiveDate?: string): Observable<boolean> {
    const today = moment().format('YYYY-MM-DD');

    return forkJoin({
      tasksRes: this.tmsService.getTasks(today, currentUser?.id, lastActiveDate).pipe(catchError(() => of(null))),
      eventsRes: this.tmsService.getCalendarEvents(today, today, lastActiveDate).pipe(catchError(() => of(null))),
    }).pipe(
      map(({ tasksRes, eventsRes }) => {
        const tasks = tasksRes?.data?.data || [];
        const events = eventsRes?.data?.events || [];
        return tasks.length > 0 || events.length > 0;
      })
    );
  }
}
